import debounce from 'lodash.debounce';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import { useContentSearchSuggestions } from '@/hooks/use-content-search-suggestions';
import { useTransformSearchSuggestions } from '@/hooks/use-transform-search-suggestions';
import type { SuggestionItem, TrendingSearchItem } from '../suggestions-panel/types';
import { RECENT_SEARCH_MIN_LENGTH, TYPING_LEFT_COLUMN_LIMIT } from '../search-constants';

export function useSearchBarSuggestions(
    query: string,
    suggestionsDebounceMs: number
): {
    trimmedQuery: string;
    refetch: () => Promise<void>;
    trendingItems: TrendingSearchItem[];
    typingLeftItems: SuggestionItem[];
    typingCategoryItems: SuggestionItem[];
} {
    const queryRef = useRef(query);
    const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve());

    const trimmedQuery = query.trim();
    const { data: suggestions, refetch } = useSearchSuggestions({
        q: trimmedQuery.length === 0 ? '' : trimmedQuery,
        expand: ['images', 'prices'],
        includeEinsteinSuggestedPhrases: true,
        enabled: trimmedQuery.length === 0 || trimmedQuery.length >= RECENT_SEARCH_MIN_LENGTH,
    });

    const transformedSuggestions = useTransformSearchSuggestions(suggestions);
    const { hits: contentHits } = useContentSearchSuggestions(trimmedQuery, true, suggestionsDebounceMs);

    useEffect(() => {
        queryRef.current = query;
        refetchRef.current = refetch;
    }, [query, refetch]);

    const debouncedRefetch = useMemo(() => {
        return debounce(() => {
            const currentQuery = queryRef.current;
            if (currentQuery.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
                void refetchRef.current();
            }
        }, suggestionsDebounceMs);
    }, [suggestionsDebounceMs]);

    useEffect(() => {
        if (query.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedRefetch();
        } else {
            debouncedRefetch.cancel();
        }

        return () => {
            debouncedRefetch.cancel();
        };
    }, [query, debouncedRefetch]);

    const trendingItems: TrendingSearchItem[] = useMemo(() => {
        if (trimmedQuery.length > 0) return [];
        const popular = transformedSuggestions?.popularSearchSuggestions ?? [];
        return popular.map((p) => ({ label: p.name, link: p.link }));
    }, [trimmedQuery, transformedSuggestions]);

    const typingLeftItems: SuggestionItem[] = useMemo(() => {
        const q = query.trim();
        if (!q || q.length < RECENT_SEARCH_MIN_LENGTH) return [];
        if (!transformedSuggestions && contentHits.length === 0) return [];

        const left: SuggestionItem[] = [];
        const products = transformedSuggestions?.productSuggestions ?? [];
        const phrases = transformedSuggestions?.phraseSuggestions ?? [];
        for (const p of products) {
            if (left.length >= TYPING_LEFT_COLUMN_LIMIT) break;
            left.push({ label: p.name, kind: 'product', link: p.link, id: p.id });
        }
        for (const content of contentHits) {
            if (left.length >= TYPING_LEFT_COLUMN_LIMIT) break;
            left.push({ label: content.title, kind: 'content', link: content.path, id: content.id });
        }
        for (const ph of phrases) {
            if (left.length >= TYPING_LEFT_COLUMN_LIMIT) break;
            left.push({ label: ph.name, kind: 'phrase', link: ph.link, id: String(phrases.indexOf(ph)) });
        }
        return left;
    }, [query, transformedSuggestions, contentHits]);

    const typingCategoryItems: SuggestionItem[] = useMemo(() => {
        const q = query.trim();
        if (!q || q.length < RECENT_SEARCH_MIN_LENGTH || !transformedSuggestions) return [];
        const categories = transformedSuggestions.categorySuggestions ?? [];
        return categories.map((c) => ({
            label: c.name,
            kind: 'category' as const,
            link: c.link,
            id: c.id,
        }));
    }, [query, transformedSuggestions]);

    return {
        trimmedQuery,
        refetch,
        trendingItems,
        typingLeftItems,
        typingCategoryItems,
    };
}
